import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import {
  ShieldCheck, Plus, Copy, ExternalLink, Trash2, ArrowLeft, MessageSquare,
  Meh, Frown, User, Loader2, Check, Edit3, QrCode, Download, Eye, MousePointerClick,
  HelpCircle, X, Star,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicOrigin = () => window.location.origin;

// ─── QR MODAL ─────────────────────────────────────────────
function QrModal({ filter, onClose }) {
  const canvasWrapRef = useRef(null);
  const link = `${publicOrigin()}/avis/${filter.slug}`;

  const download = (format = 'png') => {
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL(`image/${format}`);
    const a = document.createElement('a');
    a.href = url;
    a.download = `safe-review-${filter.slug}.${format}`;
    a.click();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="w-full max-w-sm bg-[#1A1A2E] border border-white/10 rounded-3xl p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        <div className="text-center mb-4">
          <p className="text-xs uppercase tracking-widest font-bold text-emerald-400 mb-1">QR Code</p>
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{filter.business_name}</h3>
        </div>
        <div ref={canvasWrapRef} className="bg-white rounded-2xl p-6 flex items-center justify-center mb-4" data-testid="qr-canvas">
          <QRCodeCanvas value={link} size={220} level="H" includeMargin={false} bgColor="#FFFFFF" fgColor="#0F172A" />
        </div>
        <p className="text-[11px] text-slate-500 text-center mb-4 font-mono break-all">{link}</p>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => download('png')} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm active:scale-95 transition-all" data-testid="download-qr-png">
            <Download className="w-4 h-4" /> PNG
          </button>
          <button onClick={() => download('jpeg')} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 transition-all">
            <Download className="w-4 h-4" /> JPG
          </button>
        </div>
        <p className="text-[11px] text-center text-slate-500 mt-4">Idéal pour stickers, cartes de visite, chevalets de table.</p>
      </motion.div>
    </motion.div>
  );
}

// ─── CREATE / EDIT MODAL ──────────────────────────────────
function EditModal({ token, filter, onClose, onSaved }) {
  const isNew = !filter;
  const [name, setName] = useState(filter?.business_name || '');
  const [url, setUrl] = useState(filter?.google_review_url || '');
  const [color, setColor] = useState(filter?.accent_color || '#F59E0B');
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const submit = async () => {
    if (!name.trim() || !url.trim()) { toast.error('Nom et lien Google requis'); return; }
    setBusy(true);
    try {
      const endpoint = isNew ? `${API}/vendor/review-filters` : `${API}/vendor/review-filters/${filter.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const r = await fetch(endpoint, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_name: name.trim(), google_review_url: url.trim(), accent_color: color }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur');
      toast.success(isNew ? 'Page Safe Review créée !' : 'Enregistré !');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }} className="w-full max-w-md bg-[#1A1A2E] border border-white/10 rounded-3xl p-6 my-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{isNew ? 'Nouvelle page Safe Review' : 'Modifier la page'}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Nom du commerce</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Ex : Boulangerie Chez Paul" className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500 text-sm" data-testid="filter-name-input" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Lien Google d&apos;avis</label>
              <button type="button" onClick={() => setShowHelp(s => !s)} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid="help-toggle">
                <HelpCircle className="w-3 h-3" /> Comment l&apos;obtenir ?
              </button>
            </div>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://g.page/r/..." className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500 text-sm font-mono" data-testid="filter-url-input" />
            <AnimatePresence>
              {showHelp && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <div className="mt-2 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-[11px] text-emerald-100 space-y-1.5 leading-relaxed">
                    <p className="font-bold text-emerald-300">Méthode rapide (2 min) :</p>
                    <p>1. Va sur <a href="https://www.google.com/business/" target="_blank" rel="noopener noreferrer" className="underline">google.com/business</a> et connecte-toi</p>
                    <p>2. Sélectionne la fiche → onglet <b>Avis</b> → bouton <b>Partager le formulaire d&apos;avis</b></p>
                    <p>3. Colle le lien ci-dessus (format <code>https://g.page/r/...</code>)</p>
                    <p className="pt-1.5 border-t border-emerald-500/20"><b>Pas encore de fiche ?</b> Utilise <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" className="underline">Place ID Finder</a> puis <code>https://search.google.com/local/writereview?placeid=VOTRE_ID</code></p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Couleur d&apos;accent</label>
            <div className="flex items-center gap-2">
              <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-12 h-10 rounded-lg border border-white/10 cursor-pointer bg-transparent" data-testid="filter-color-input" />
              <div className="flex-1 grid grid-cols-5 gap-1.5">
                {['#F59E0B', '#10B981', '#3B82F6', '#EC4899', '#8B5CF6'].map(c => (
                  <button key={c} type="button" onClick={() => setColor(c)} className={`h-10 rounded-lg border-2 transition-all ${color === c ? 'border-white scale-105' : 'border-transparent'}`} style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-sm font-semibold hover:bg-white/10">Annuler</button>
            <button onClick={submit} disabled={busy} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-bold disabled:opacity-50 active:scale-95 transition-all" data-testid="filter-save-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {isNew ? 'Créer' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── LIST ─────────────────────────────────────────────────
function FiltersList({ token, onOpen }) {
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | filter obj
  const [qrFilter, setQrFilter] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/vendor/review-filters`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setFilters(d.filters || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette page et tous ses avis reçus ?')) return;
    const r = await fetch(`${API}/vendor/review-filters/${id}`, { method: 'DELETE', headers: authHeaders });
    if (!r.ok) { toast.error('Échec de la suppression'); return; }
    toast.success('Supprimé');
    load();
  };

  const copyLink = (slug) => {
    const link = `${publicOrigin()}/avis/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien copié !');
  };

  // Stats totals
  const totals = filters.reduce((acc, f) => ({
    visits: acc.visits + (f.visits || 0),
    clicks: acc.clicks + (f.google_clicks || 0),
    feedback: acc.feedback + (f.feedback_count || 0),
    unread: acc.unread + (f.unread_count || 0),
  }), { visits: 0, clicks: 0, feedback: 0, unread: 0 });

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-6 h-6 text-emerald-400" />
            <h1 className="text-2xl sm:text-3xl font-black text-white" style={{ fontFamily: 'Outfit' }}>Safe Review</h1>
          </div>
          <p className="text-sm text-slate-400" style={{ fontFamily: 'Outfit' }}>Filtrez les avis positifs vers Google, capturez les négatifs en interne.</p>
        </div>
        <button onClick={() => setEditing('new')} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm transition-all active:scale-95" data-testid="new-filter-btn">
          <Plus className="w-4 h-4" /> Nouvelle page
        </button>
      </div>

      {/* Stats bar */}
      {filters.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="stats-bar">
          {[
            { icon: Eye, label: 'Visites', value: totals.visits, color: 'text-blue-400' },
            { icon: MousePointerClick, label: 'Clics Google', value: totals.clicks, color: 'text-emerald-400' },
            { icon: MessageSquare, label: 'Avis privés', value: totals.feedback, color: 'text-amber-400' },
            { icon: Star, label: 'Non lus', value: totals.unread, color: 'text-pink-400' },
          ].map((s, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.label}</span>
              </div>
              <p className="text-xl font-black text-white">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {editing && <EditModal token={token} filter={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={load} />}
        {qrFilter && <QrModal filter={qrFilter} onClose={() => setQrFilter(null)} />}
      </AnimatePresence>

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-slate-500 text-sm"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Chargement...</div>
      ) : filters.length === 0 ? (
        <div className="py-20 text-center bg-white/5 rounded-3xl border border-white/10 border-dashed">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-emerald-400" />
          </div>
          <p className="text-white font-bold text-base mb-1" style={{ fontFamily: 'Outfit' }}>Créez votre première page Safe Review</p>
          <p className="text-xs text-slate-500 mb-5 max-w-sm mx-auto">Un lien + un QR code prêt à imprimer en moins de 30 secondes.</p>
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm active:scale-95 transition-all" data-testid="empty-create-btn">
            <Plus className="w-4 h-4" /> Créer maintenant
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {filters.map(f => (
            <div key={f.id} className="bg-white/5 hover:bg-white/8 border border-white/10 rounded-2xl p-4 transition-all" data-testid={`filter-row-${f.slug}`}>
              <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-slate-900 font-black text-lg flex-shrink-0" style={{ background: `linear-gradient(135deg, ${f.accent_color || '#F59E0B'}, ${f.accent_color || '#F59E0B'}dd)` }}>
                  {f.business_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-sm truncate" style={{ fontFamily: 'Outfit' }}>{f.business_name}</p>
                  <p className="text-[11px] text-slate-400 truncate font-mono">{publicOrigin()}/avis/{f.slug}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {f.visits || 0}</span>
                    <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {f.google_clicks || 0}</span>
                    <button onClick={() => onOpen(f)} className="flex items-center gap-1 hover:text-white transition-colors">
                      <MessageSquare className="w-3 h-3" /> {f.feedback_count || 0}
                      {f.unread_count > 0 && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-red-500" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => setQrFilter(f)} title="QR Code" className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/20 transition-all" data-testid={`qr-${f.slug}`}>
                    <QrCode className="w-4 h-4" />
                  </button>
                  <button onClick={() => copyLink(f.slug)} title="Copier le lien" className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:bg-white/10 transition-all" data-testid={`copy-${f.slug}`}>
                    <Copy className="w-4 h-4" />
                  </button>
                  <a href={`/avis/${f.slug}`} target="_blank" rel="noopener noreferrer" title="Ouvrir" className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:bg-white/10 transition-all">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => setEditing(f)} title="Modifier" className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:bg-white/10 transition-all" data-testid={`edit-${f.slug}`}>
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => onOpen(f)} title="Avis privés" className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:bg-white/10 transition-all" data-testid={`view-${f.slug}`}>
                    <MessageSquare className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(f.id)} title="Supprimer" className="w-9 h-9 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all" data-testid={`delete-${f.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── FEEDBACKS DETAIL ─────────────────────────────────────
function FeedbacksView({ token, filter, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/vendor/review-filters/${filter.id}/feedbacks`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter.id, token]);
  useEffect(() => { load(); }, [load]);

  const remove = async (fbId) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    const r = await fetch(`${API}/vendor/review-filters/${filter.id}/feedbacks/${fbId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { toast.error('Échec de la suppression'); return; }
    load();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-5 transition-colors" data-testid="back-to-filters">
        <ArrowLeft className="w-4 h-4" /> Retour à la liste
      </button>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest font-bold text-emerald-400 mb-1">Avis privés reçus</p>
        <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>{filter.business_name}</h1>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : !data?.feedbacks?.length ? (
        <div className="py-16 text-center bg-white/5 rounded-2xl border border-white/10">
          <MessageSquare className="w-10 h-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-300 font-semibold">Aucun avis privé pour l&apos;instant</p>
          <p className="text-xs text-slate-500 mt-1">Les clients insatisfaits laisseront ici leurs remarques (jamais visibles publiquement)</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {data.feedbacks.map(fb => (
            <div key={fb.id} className={`p-5 rounded-2xl border ${fb.mood === 'sad' ? 'bg-red-500/5 border-red-500/20' : 'bg-amber-500/5 border-amber-500/20'}`} data-testid={`feedback-${fb.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${fb.mood === 'sad' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                  {fb.mood === 'sad' ? <Frown className="w-5 h-5" /> : <Meh className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">{fb.name || 'Client anonyme'}</p>
                  <p className="text-[11px] text-slate-500">{new Date(fb.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${fb.mood === 'sad' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                  {fb.mood === 'sad' ? 'Pas content' : 'Moyen'}
                </span>
                <button onClick={() => remove(fb.id)} title="Supprimer" className="w-8 h-8 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all" data-testid={`delete-fb-${fb.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {fb.message && <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-line mb-2">{fb.message}</p>}
              {fb.contact && (
                <div className="flex items-center gap-2 text-xs text-slate-400 mt-2 pt-2 border-t border-white/5">
                  <User className="w-3 h-3" /> {fb.contact}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────
export default function SafeReviewPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (!token) navigate('/login');
    else if (user && !['vendor', 'admin', 'merchant'].includes(user.role)) navigate('/');
  }, [user, token, navigate]);

  if (!token || !user) return null;

  const dashboardPath = user.role === 'admin' ? '/admin' : user.role === 'merchant' ? '/dashboard' : '/vendor';

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0F0F1A 0%, #1A1A2E 100%)' }}>
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate(dashboardPath)} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-testid="header-back">
            <ArrowLeft className="w-4 h-4" /> Retour au dashboard
          </button>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span>Safe Review</span>
          </div>
        </div>
      </header>

      <AnimatePresence mode="wait">
        {current ? (
          <motion.div key="detail" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <FeedbacksView token={token} filter={current} onBack={() => setCurrent(null)} />
          </motion.div>
        ) : (
          <motion.div key="list" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
            <FiltersList token={token} onOpen={setCurrent} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
